import React, { useCallback, useState } from 'react';
import { Alert, FlatList, RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { generateLeaveBalances, getLeaveConfigurations, deactivateLeaveConfiguration } from '../../services/leaveConfigurationService';
import type { LeaveConfiguration } from '../../types/leaveConfiguration';
import { CONFIGURATION_RECORD_ACTIVE } from '../../constants/configuration';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { isActiveConfigurationRecord } from '../../utils/configurationFilters';
import { useConfigurationSoftDelete } from '../../hooks/useConfigurationSoftDelete';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { normalizeApiError } from '../../api/apiError';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'LeaveConfigurationList'>;
const YEAR = new Date().getFullYear();

export function LeaveConfigurationListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const [items, setItems] = useState<LeaveConfiguration[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const softDelete = useConfigurationSoftDelete();

  const load = useCallback(async (refresh = false, term = search) => {
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      setItems((await getLeaveConfigurations(term, { isActive: CONFIGURATION_RECORD_ACTIVE })).items.filter(item => isActiveConfigurationRecord(item.isActive)));
      setError(null);
    } catch (caught) {
      setError(normalizeApiError(caught).message || 'Unable to load leave configurations.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [search]);

  useFocusEffect(useCallback(() => { load(false).catch(() => undefined); }, [load]));

  async function generate(item: LeaveConfiguration) {
    setBusyId(item.leavePolicyId);
    try {
      const result = await generateLeaveBalances(item.leavePolicyId, YEAR);
      Alert.alert('Balances generated', `${result.createdCount} employee balance(s) created for ${YEAR}.`);
      await load(true);
    } catch (caught) {
      Alert.alert('Generation failed', normalizeApiError(caught).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <PermissionGuard permission={PERMISSIONS.SETTINGS_VIEW} fallback={<UnauthorizedScreen />}>
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader title="Leave configuration" onBack={navigation.goBack} />
        <View style={styles.body}>
          <AppInput label="Search" value={search} onChangeText={setSearch} placeholder="Leave or policy name" />
          <AppButton title="Search" variant="secondary" onPress={() => load(false)} />
          <AppButton title="Create leave type" onPress={() => navigation.navigate(routes.leaveConfigurationCreate)} />
          {error && items.length === 0 ? <ErrorState message={error} onRetry={() => load()} /> : (
            <FlatList
              data={items}
              keyExtractor={item => String(item.leavePolicyId)}
              refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />}
              contentContainerStyle={styles.list}
              ListEmptyComponent={!loading ? <EmptyState title="No leave types configured" message="Create the first leave type and annual entitlement." /> : null}
              renderItem={({ item }) => (
                <AppCard style={styles.card}>
                  <View style={styles.cardHeader}>
                    <Text style={[styles.title, { color: palette.textPrimary, flex: 1 }]}>{formatDisplayLabel(item.leaveTypeName)}</Text>
                    <ConfigurationDeleteButton
                      placement="inline"
                      entityLabel="leave type"
                      loading={softDelete.busy && busyId === item.leavePolicyId}
                      disabled={busyId != null || softDelete.busy}
                      onPress={() =>
                        softDelete.confirmAndDelete({
                          entityLabel: 'leave type',
                          recordName: formatDisplayLabel(item.leaveTypeName),
                          deleteRecord: async () => {
                            setBusyId(item.leavePolicyId);
                            try {
                              await deactivateLeaveConfiguration(item);
                              setItems(current =>
                                current.filter(row => row.leavePolicyId !== item.leavePolicyId),
                              );
                            } finally {
                              setBusyId(null);
                            }
                          },
                          onSuccess: () => {
                            load(true).catch(() => undefined);
                          },
                          errorMessage: caught =>
                            normalizeApiError(caught).message ||
                            'Unable to delete leave configuration.',
                        })
                      }
                    />
                  </View>
                  <Text style={[styles.bodyText, { color: palette.textSecondary }]}>{formatDisplayLabel(item.leaveTypeCode)} · {formatDisplayLabel(item.leaveCategory)}</Text>
                  <Text style={[styles.days, { color: palette.primary }]}>{item.annualEntitlement} days per year</Text>
                  <Text style={[styles.bodyText, { color: palette.textSecondary }]}>{item.monthlyAccrual} leave(s) per month</Text>
                  <Text style={[styles.bodyText, { color: palette.textSecondary }]}>{item.currentYearEmployeeCount} employee balance(s) for {YEAR}</Text>
                  <AppButton
                    title="Edit configuration"
                    variant="secondary"
                    onPress={() => navigation.navigate(routes.leaveConfigurationCreate, { configuration: item })}
                  />
                  {item.requiresBalance ? <AppButton title={`Generate ${YEAR} balances`} variant="secondary" loading={busyId === item.leavePolicyId} disabled={busyId != null || softDelete.busy} onPress={() => generate(item)} /> : null}
                </AppCard>
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  list: { flexGrow: 1, gap: spacing.sm, paddingBottom: spacing.xl },
  card: { gap: spacing.xs },
  cardHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  title: typography.sectionTitle,
  bodyText: typography.body,
  days: { ...typography.body, fontWeight: '700' },
});
